import React from 'react';

/**
 * FormsTableSkeleton - Loading skeleton for the Forms tab
 * @param {string} viewMode - 'table' or 'grid' view mode
 */
export const FormsTableSkeleton = ({ viewMode = 'table' }) => {
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-surface rounded-lg border border-border p-6 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
              </div>
              <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
            </div>

            {/* Description */}
            <div className="space-y-2 mb-4">
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-4/5"></div>
            </div>

            {/* Stats */}
            <div className="flex items-center space-x-4 mb-4">
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-28"></div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded flex-1"></div>
              <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Table view layout
  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-secondary-50">
              <th className="px-6 py-4 text-left">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-28 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-right">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16 ml-auto animate-pulse"></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-border hover:bg-secondary-50 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-40 mb-1"></div>
                  <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2 justify-end">
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * JobsTableSkeleton - Loading skeleton for the Jobs tab
 * @param {string} viewMode - 'table' or 'grid' view mode
 */
export const JobsTableSkeleton = ({ viewMode = 'table' }) => {
  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-surface rounded-lg border border-border p-6 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-1/2"></div>
              </div>
              <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16 ml-2"></div>
            </div>

            {/* Description */}
            <div className="space-y-2 mb-4">
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-5/6"></div>
            </div>

            {/* Skills */}
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
              <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-24"></div>
              <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
            </div>

            {/* Footer Stats */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="space-y-2 flex-1">
                <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16"></div>
              </div>
              <div className="flex gap-2">
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Table view layout
  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-background">
              <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 animate-pulse"></div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16 animate-pulse"></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-border hover:bg-background animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-40"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * ApplicationsGridSkeleton - Loading skeleton for the Applications tab
 * @param {string} viewMode - 'grid' or 'list' view mode
 */
export const ApplicationsGridSkeleton = ({ viewMode = 'grid' }) => {
  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-surface rounded-lg border border-border p-4 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-40 mb-2"></div>
                    <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32"></div>
                  </div>
                  <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16 ml-2"></div>
                </div>

                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-16"></div>
                  <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
                </div>

                {/* Match and Rate */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-28"></div>
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-shrink-0">
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Grid view layout
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="bg-surface rounded-lg border border-border p-6 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full mb-2"></div>
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
              </div>
            </div>
            <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-14 ml-2"></div>
          </div>

          {/* Job Title */}
          <div className="mb-3">
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-2/3"></div>
          </div>

          {/* Skills */}
          <div className="flex flex-wrap gap-2 mb-4">
            <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-16"></div>
            <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
          </div>

          {/* Match Bar */}
          <div className="mb-4">
            <div className="h-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-full"></div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
            <div className="flex gap-2">
              <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
              <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * HiredVAsListSkeleton - Loading skeleton for the Hired VAs tab
 * @param {string} viewMode - 'grid' or 'list' view mode
 */
export const HiredVAsListSkeleton = ({ viewMode = 'grid' }) => {
  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-surface rounded-lg border border-border p-4 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-40 mb-2"></div>
                    <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32"></div>
                  </div>
                  <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20 ml-2"></div>
                </div>

                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-16"></div>
                  <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
                </div>

                {/* Contract Info */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-28"></div>
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-shrink-0">
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Grid view layout
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-surface rounded-lg border border-border p-6 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
          {/* Avatar */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full"></div>
          </div>

          {/* Name and Title */}
          <div className="text-center mb-4">
            <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4 mx-auto mb-2"></div>
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-2/3 mx-auto"></div>
          </div>

          {/* Rating */}
          <div className="flex justify-center mb-4 gap-1">
            {Array.from({ length: 5 }).map((_, j) => (
              <div key={j} className="h-5 w-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
            ))}
          </div>

          {/* Skills */}
          <div className="flex flex-wrap gap-2 mb-4 justify-center">
            <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-16"></div>
            <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-20"></div>
          </div>

          {/* Contract Info */}
          <div className="space-y-2 mb-4 text-center">
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 mx-auto"></div>
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20 mx-auto"></div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-center pt-4 border-t border-border">
            <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
            <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-8"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * PerformanceAnalyticsSkeleton - Loading skeleton for the Analytics tab
 */
export const PerformanceAnalyticsSkeleton = () => {
  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-surface rounded-lg border border-border p-6 animate-pulse" style={{ animationDelay: `${i * 100}ms` }}>
            <div className="flex items-start justify-between mb-4">
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24"></div>
                <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
              </div>
              <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg"></div>
            </div>
            <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16"></div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart 1 */}
        <div className="bg-surface rounded-lg border border-border p-6 animate-pulse" style={{ animationDelay: '400ms' }}>
          <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32 mb-4"></div>
          <div className="h-64 bg-gradient-to-r from-gray-100 to-gray-200 rounded"></div>
        </div>

        {/* Chart 2 */}
        <div className="bg-surface rounded-lg border border-border p-6 animate-pulse" style={{ animationDelay: '500ms' }}>
          <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32 mb-4"></div>
          <div className="h-64 bg-gradient-to-r from-gray-100 to-gray-200 rounded"></div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-surface rounded-lg border border-border overflow-hidden animate-pulse" style={{ animationDelay: '600ms' }}>
        <div className="p-6">
          <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32 mb-4"></div>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-border">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32"></div>
                <div className="flex gap-4">
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
